import React from 'react';
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';
import SEO from '@/components/SEO';
import { useNavigate } from 'react-router-dom';
import { Sparkles, Shield, Droplets, Lightbulb, Scissors, Car, Package, Zap } from 'lucide-react';

const ServicesPage = () => {
  const navigate = useNavigate();

  const services = [
    {
      icon: <Sparkles className="w-12 h-12" />,
      title: '1-Step Paint Correction',
      description: 'Single-stage machine polishing to remove light swirl marks, minor scratches, and oxidation. Perfect for vehicles in good condition that need a refresh.',
      benefits: ['Removes 50-70% of imperfections', 'Enhanced gloss and clarity', 'Quick turnaround', 'Ideal for newer vehicles'],
    },
    {
      icon: <Sparkles className="w-12 h-12" />,
      title: '2-Step Paint Correction',
      description: 'Advanced two-stage polishing process for heavily damaged paint. Removes deep scratches, severe swirl marks, and restores a showroom finish.',
      benefits: ['Removes 85-95% of defects', 'Mirror-like finish', 'Removes heavy oxidation', 'Best for aged or neglected paint'],
    },
    {
      icon: <Scissors className="w-12 h-12" />,
      title: 'Scratch Removal',
      description: 'Targeted scratch repair and blending. We assess each scratch and use specialized techniques to minimize or eliminate visible damage.',
      benefits: ['Surface-level scratch removal', 'Paint leveling', 'Color matching', 'Improved appearance'],
    },
    {
      icon: <Zap className="w-12 h-12" />,
      title: 'Touch-Up Paint (Stone Chip Restoration)',
      description: 'Professional stone chip repair using color-matched paint. Protects exposed metal from rust and restores a smooth surface.',
      benefits: ['Prevents rust formation', 'Color-matched application', 'Smooth finish', 'Protects paint integrity'],
    },
    {
      icon: <Shield className="w-12 h-12" />,
      title: 'Ceramic Coating',
      description: 'Premium ceramic coating application providing long-lasting protection and hydrophobic properties. Creates a durable, glossy shield against contaminants.',
      benefits: ['3-5 years protection', 'UV resistance', 'Hydrophobic water beading', 'Easy maintenance', 'Enhanced gloss'],
    },
    {
      icon: <Lightbulb className="w-12 h-12" />,
      title: 'Headlight Restoration',
      description: 'Restore yellowed, foggy, or oxidized headlights to crystal-clear condition. Improves visibility and enhances vehicle appearance.',
      benefits: ['Improved night visibility', 'UV protective sealant', 'Removes oxidation', 'Like-new clarity'],
    },
    {
      icon: <Droplets className="w-12 h-12" />,
      title: 'Interior Deep Cleaning',
      description: 'Comprehensive interior detailing including vacuuming, steam cleaning, leather conditioning, and stain removal. All surfaces thoroughly cleaned.',
      benefits: ['Steam cleaning', 'Leather conditioning', 'Stain removal', 'Odor elimination', 'Fabric protection'],
    },
    {
      icon: <Car className="w-12 h-12" />,
      title: 'Exterior Wash + Tire Shine',
      description: 'Complete exterior hand wash with wheel cleaning, tire dressing, and exterior trim restoration. Perfect for regular maintenance.',
      benefits: ['Hand wash technique', 'Wheel & tire cleaning', 'Tire shine application', 'Window cleaning'],
    },
    {
      icon: <Package className="w-12 h-12" />,
      title: 'Full Vehicle Polishing Packages',
      description: 'Complete detailing packages combining interior, exterior, paint correction, and protection services. Customized to your vehicle\'s needs.',
      benefits: ['Comprehensive service', 'Custom packages', 'Interior + Exterior', 'Maximum value'],
    },
  ];

  return (
    <div data-testid="services-page" className="min-h-screen bg-black">
      <SEO 
        title="Our Services – ONYXA Car Detailing"
        description="Professional car detailing services including paint correction, ceramic coating, interior detailing, and more. Expert mobile detailing in NYC & Tri-State area."
        canonicalPath="/services"
      />
      <Navigation />

      {/* Hero Section */}
      <section data-testid="services-hero" className="relative pt-32 pb-20 px-6 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-black via-gray-900 to-black"></div>
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 right-20 w-96 h-96 bg-red-600 rounded-full filter blur-[150px]"></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto text-center">
          <h1 
            data-testid="services-hero-title"
            className="text-5xl lg:text-6xl font-bold mb-6"
            style={{ fontFamily: 'Cormorant Garamond, serif' }}
          >
            Our <span className="text-red-500">Services</span>
          </h1>
          <p data-testid="services-hero-subtitle" className="text-lg text-gray-300 max-w-3xl mx-auto">
            Professional car detailing services delivered to your location. All pricing is customized based on your vehicle's condition and needs.
          </p>
        </div>
      </section>

      {/* Services Grid */}
      <section data-testid="services-grid-section" className="py-12 px-6">
        <div className="max-w-7xl mx-auto">
          <div data-testid="services-list" className="space-y-8">
            {services.map((service, index) => (
              <div
                key={index}
                data-testid={`service-detail-${index}`}
                className="glass-card p-8 hover:scale-[1.02] transition-transform duration-300"
              >
                <div className="flex flex-col md:flex-row gap-6">
                  <div className="text-red-500 flex-shrink-0">
                    {service.icon}
                  </div>
                  <div className="flex-grow">
                    <h3 className="text-2xl font-bold mb-3">{service.title}</h3>
                    <p className="text-gray-300 mb-4">{service.description}</p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      {service.benefits.map((benefit, i) => (
                        <div key={i} className="flex items-center space-x-2 text-sm text-gray-400">
                          <div className="w-1.5 h-1.5 bg-red-500 rounded-full"></div>
                          <span>{benefit}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Service Area */}
      <section data-testid="service-area-section" className="py-20 px-6 bg-gradient-to-b from-black to-gray-900">
        <div className="max-w-4xl mx-auto text-center">
          <h2 
            data-testid="service-area-title"
            className="text-4xl font-bold mb-6"
            style={{ fontFamily: 'Cormorant Garamond, serif' }}
          >
            Service Area
          </h2>
          <p className="text-lg text-gray-300 mb-4">
            We proudly serve <span className="text-red-500 font-semibold">Brooklyn, NY and nearby areas</span>
          </p>
          <p className="text-gray-400">
            Our mobile service brings professional detailing directly to your home, office, or preferred location.
          </p>
        </div>
      </section>

      {/* CTA Section */}
      <section data-testid="services-cta-section" className="py-20 px-6">
        <div className="max-w-4xl mx-auto text-center glass-card p-12 rounded-3xl">
          <h2 
            data-testid="services-cta-title"
            className="text-4xl font-bold mb-6"
            style={{ fontFamily: 'Cormorant Garamond, serif' }}
          >
            Get Custom Pricing
          </h2>
          <p className="text-lg text-gray-300 mb-8">
            Every vehicle is unique. Contact us for personalized pricing based on your vehicle's condition and service needs.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              data-testid="services-cta-book-btn"
              onClick={() => navigate('/booking')}
              className="btn-primary"
            >
              Book Appointment
            </button>
            <button
              data-testid="services-cta-quote-btn"
              onClick={() => navigate('/contact')}
              className="btn-secondary"
            >
              Request Quote
            </button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ServicesPage;
